import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, ShoppingCart, BookMarked } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const euros = (c) => (c / 100).toFixed(2).replace(".", ",") + " €";
const coverSrc = (c) => (c.cover_url ? `${BACKEND}${c.cover_url}` : c.book_cover || `${BACKEND}/api/game/ile/image/3`);

export default function CarnetsBoutique() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [carnets, setCarnets] = useState([]);

  useEffect(() => { api.get("/carnets").then((r) => setCarnets(r.data || [])); }, []);

  const buyPdf = async (c) => {
    if (!authed) { sessionStorage.setItem("tao_unlock_book", `carnet-pdf:${c.id}`); navigate("/connexion?next=/boutique/carnets"); return; }
    try {
      const { data } = await api.post("/payments/checkout", { product: "carnet", carnet_id: c.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 400) toast.success("Carnet déjà acheté");
      else toast.error("Paiement impossible");
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-[#1F2A24] text-[#F6F2E9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-16 md:py-20 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold">La Boutique</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Les Carnets</h1>
          <div className="gold-divider w-24 mx-auto my-6" />
          <p className="font-sans text-sm text-[#F6F2E9]/75 max-w-xl mx-auto">
            Chaque carnet existe en deux formats : <strong>PDF téléchargeable</strong> (accès immédiat) ou <strong>livre broché</strong> sur Amazon.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        {carnets.length === 0 ? (
          <p className="text-center font-sans text-[#1F2A24]/60" data-testid="carnets-empty">Les carnets seront bientôt disponibles.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="carnets-boutique">
            {carnets.map((c) => (
              <div key={c.id} data-testid={`boutique-carnet-${c.id}`} className="rounded-2xl bg-white border border-[#E8DCC4] overflow-hidden flex flex-col">
                <div className="aspect-[16/10] relative overflow-hidden bg-[#385E3A]">
                  <img src={coverSrc(c)} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(31,42,36,0.88) 0%, rgba(31,42,36,0.4) 42%, rgba(31,42,36,0) 70%)" }} />
                  <div className="absolute bottom-0 p-4">
                    {c.is_foundation ? <span className="font-serif text-lg text-[#C9A24B]">Livre fondateur</span> : c.number != null && <span className="font-serif text-lg text-[#C9A24B]">Carnet {c.number}</span>}
                    <p className="font-serif text-xl text-white leading-tight">{c.title || c.name}</p>
                  </div>
                </div>
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div className="rounded-xl border border-[#E8DCC4] p-3 flex items-center justify-between">
                    <div>
                      <p className="font-sans text-xs text-[#1F2A24]/60">📥 PDF téléchargeable</p>
                      <p className="font-serif text-lg text-[#1F2A24]">{euros(c.price_cents)}</p>
                    </div>
                    <button onClick={() => buyPdf(c)} data-testid={`buy-pdf-${c.id}`} disabled={!c.active_sale}
                      className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-4 py-2.5 font-ui text-sm hover:bg-[#2c4a2e] disabled:opacity-40">
                      <ShoppingCart size={15} /> Acheter le PDF
                    </button>
                  </div>
                  <div className="rounded-xl border border-[#E8DCC4] p-3 flex items-center justify-between">
                    <div>
                      <p className="font-sans text-xs text-[#1F2A24]/60">📕 Livre broché Amazon</p>
                      {c.amazon_price && <p className="font-serif text-lg text-[#1F2A24]" data-testid={`amazon-price-${c.id}`}>{c.amazon_price}</p>}
                    </div>
                    {c.amazon_url ? (
                      <a href={c.amazon_url} target="_blank" rel="noreferrer" data-testid={`buy-amazon-${c.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-4 py-2.5 font-ui text-sm hover:bg-[#b8923f]">
                        <BookMarked size={15} /> Acheter
                      </a>
                    ) : <span className="font-sans text-xs text-[#1F2A24]/40">Bientôt</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
